import { rephraseText } from '@commenda/api';
import type { IChatRoomSubscriberCandidate } from '@commenda/api/chatRoom/types';
import {
  Dialog,
  DialogContent,
  IconButton,
  Icons,
  Tooltip,
  useToggle,
} from '@commenda/kakashi';
import mixpanel, {
  MIXPANEL_EVENTS,
  MIXPANEL_SURFACE_AREA,
} from '@commenda/mixpanel';
import { ChatRoomMessageType, LegacyDocumentType } from '@commenda/prisma';
import { IChatRoomMessage } from '@commenda/types/src/chat-room/types';
import { cn } from '@commenda/utils';
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useNotification } from '../../../hooks/useNotification';
import { ChatProps, NativeFile } from '../../ChatRoom/types';
import { THREADS_CHAT_TEMPLATES, TTemplate } from '../../TEMPLATES';
import TemplatesModal from '../../TemplatesModal';
import { AttachmentChip, AttachmentChipProps } from './AttachmentChip';
import ChatSendButton from './ChatSendButton';
import { collectThreadMentions } from './mentionExtension';
import {
  isSelectionAPlaceholder,
  selectFirstPlaceholder,
} from './placeholderNavExtension';
import { ReplyMessagePreview } from './ReplyMessagePreview';
import type { RichTextEditorHandle } from './richTextEditor';
import { useChatDraft } from './useChatDraft';

// Lazy-loaded so TipTap doesn't ship in bundles that never render the composer.
const RichTextEditor = lazy(() => import('./richTextEditor'));

interface ChatInputProps {
  onSend: ChatProps['onSend'];
  onFileUpload: ChatProps['onFileUpload'];
  selectedReplyMessage: IChatRoomMessage | null;
  removeSelectedReplyMessage: () => void;
  isChatClosed: boolean;
  shouldHaveAdditionalControls: boolean;
  initialContent?: string;
  /** sessionStorage key for the unsent draft; `null` disables draft persistence. */
  inputDraftStorageKey: string | null;
  /** Mentionable people for this thread. With `canMentionAgent` off too, the @ trigger offers nothing. */
  mentionCandidates?: IChatRoomSubscriberCandidate[];
  canMentionAgent?: boolean;
}

const ChatInput = ({
  onSend,
  onFileUpload,
  selectedReplyMessage,
  removeSelectedReplyMessage,
  isChatClosed,
  shouldHaveAdditionalControls,
  initialContent,
  inputDraftStorageKey,
  mentionCandidates,
  canMentionAgent,
}: ChatInputProps) => {
  const { content, setContent } = useChatDraft(
    inputDraftStorageKey,
    initialContent
  );
  const [files, setFiles] = useState<NativeFile[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [maxTextareaHeight, setMaxTextareaHeight] = useState(250);
  const [isPlaceholderSelected, setIsPlaceholderSelected] = useState(false);
  const [showFormattingToolbar, setShowFormattingToolbar] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const templatesModalVisibility = useToggle(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonsRef = useRef<HTMLDivElement>(null);
  const inlineEditorRef = useRef<RichTextEditorHandle>(null);
  const modalEditorRef = useRef<RichTextEditorHandle>(null);
  // Handlers that interact with the editor read whichever ref points to the
  // active surface — modal when expanded, inline otherwise. Refs themselves
  // are stable; only the selection between them flips.
  const editorRef = isExpanded ? modalEditorRef : inlineEditorRef;
  const { setNotification } = useNotification();

  // Dynamically calculate available height for textarea
  useLayoutEffect(() => {
    const container = containerRef.current;
    const buttons = buttonsRef.current;
    if (!container || !buttons) return;

    const calculateMaxHeight = () => {
      const buttonsHeight = buttons.offsetHeight;
      let chatViewContainer = container.parentElement;
      while (
        chatViewContainer &&
        !chatViewContainer.classList.contains('h-full')
      ) {
        chatViewContainer = chatViewContainer.parentElement;
      }
      const chatViewHeight =
        chatViewContainer?.clientHeight || window.innerHeight * 0.5;
      const reservedForMessages = chatViewHeight * 0.4;
      const padding = 80;
      const availableHeight =
        chatViewHeight - reservedForMessages - buttonsHeight - padding;
      const maxHeight = Math.max(150, Math.min(availableHeight, 500));
      setMaxTextareaHeight(maxHeight);
    };

    calculateMaxHeight();
    const resizeObserver = new ResizeObserver(calculateMaxHeight);
    resizeObserver.observe(container);
    window.addEventListener('resize', calculateMaxHeight);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', calculateMaxHeight);
    };
  }, []);

  function hasPendingFileUploads() {
    return files?.some?.((file) => file.isUploading);
  }

  function hasFailedFileUploads() {
    return files?.some?.((file) => !!file.errorMessage);
  }

  const onSelectTemplate = (template: TTemplate) => {
    templatesModalVisibility.hide();
    setContent(template.description);
    mixpanel.track(MIXPANEL_EVENTS.STANDARD_TEMPLATE_USED, {
      surface_area: MIXPANEL_SURFACE_AREA.THREADS,
      template_title: template.title,
    });

    // Defer until after the editor has synced to the new content (via its
    // useEffect), then select the first `[placeholder]` so the agent can type
    // straight into it.
    setTimeout(() => {
      const editor = editorRef.current?.getEditor();
      if (!editor) return;
      const selected = selectFirstPlaceholder(editor);
      setIsPlaceholderSelected(selected);
      if (!selected) {
        editorRef.current?.focus();
      }
    }, 0);
  };

  async function handleSend() {
    if (isLoading) return;

    if (hasPendingFileUploads()) {
      setNotification({
        show: true,
        valid: false,
        title: 'File upload still in progress',
        message:
          'Some of the files are still uploading, please wait before you can send them.',
      });
      return;
    }

    const hasFailedUploads = hasFailedFileUploads();
    if (hasFailedUploads) {
      setNotification({
        show: true,
        valid: false,
        title: 'Some attachments failed to upload',
        message: 'Retry or remove them before sending this message.',
      });
      return;
    }

    setIsLoading(true);

    // Read off the live editor rather than the markdown: the ids live on the mention nodes.
    const { mentionedUserIds, mentionsAgent } = collectThreadMentions(
      editorRef.current?.getEditor()?.getJSON()
    );

    let onSendSucceeded = false;
    if (!files?.length) {
      onSendSucceeded = await onSend({
        messageType: ChatRoomMessageType.TEXT,
        message: content,
        files: [],
        replyToMessageId: selectedReplyMessage?.id,
        mentionedUserIds,
        mentionsAgent,
      });
    } else {
      onSendSucceeded = await onSend({
        messageType: ChatRoomMessageType.TEXT_WITH_DOCUMENT,
        message: content,
        replyToMessageId: selectedReplyMessage?.id,
        files: files.map((file) => ({
          fileName: file.nativeFile.name,
          fileUrl: file.fileUrl,
          documentType: LegacyDocumentType.Other,
        })),
        mentionedUserIds,
        mentionsAgent,
      });
    }
    if (onSendSucceeded) {
      removeSelectedReplyMessage();
      setContent('');
      setFiles(null);
      setIsExpanded(false);
    }
    setIsLoading(false);
  }

  const handleFileUpload = useCallback(
    async (id: string) => {
      const file = files.find((eachFile) => eachFile.localId === id);
      if (!file) return;

      const markFailed = (errorMessage: string) => {
        setFiles((prev) =>
          prev.map((eachFile) =>
            eachFile.localId === id
              ? { ...eachFile, isUploading: false, errorMessage }
              : eachFile
          )
        );
        setNotification({
          show: true,
          valid: false,
          title: 'Failed to upload file',
          message: errorMessage,
        });
      };

      try {
        const res = await onFileUpload(file.nativeFile);

        if (!res?.fileUrl) {
          markFailed(res?.errorMessage ?? 'Upload failed.');
          return;
        }

        setFiles((prev) => {
          const copy = [...prev];
          const index = copy.findIndex((eachFile) => eachFile.localId === id);
          if (index === -1) return prev;
          copy[index].isUploading = false;
          copy[index].fileUrl = res.fileUrl;
          return copy;
        });
      } catch (error) {
        markFailed(error instanceof Error ? error.message : 'Upload failed.');
      }
    },
    [files, onFileUpload, setNotification]
  );

  function handleFileRemove(id: string) {
    setFiles((prev) => prev.filter((eachFile) => eachFile.localId !== id));
  }

  function handleFileRetry(id: string) {
    setFiles((prev) =>
      prev.map((eachFile) =>
        eachFile.localId === id
          ? { ...eachFile, errorMessage: undefined }
          : eachFile
      )
    );
  }

  function getAttachmentStatus(
    file: NativeFile
  ): AttachmentChipProps['status'] {
    if (file.errorMessage) return 'error';
    if (file.isUploading) return 'uploading';
    return 'done';
  }

  useEffect(() => {
    if (!files || !files.length) return;

    const filesToProcess = files.filter(
      (file) => !file.isUploading && !file.fileUrl && !file.errorMessage
    );

    if (filesToProcess.length === 0) return;

    setFiles((prevFiles) =>
      prevFiles.map((file) => {
        if (!file.isUploading && !file.fileUrl && !file.errorMessage) {
          return { ...file, isUploading: true };
        }
        return file;
      })
    );

    filesToProcess.forEach((file) => {
      handleFileUpload(file.localId);
    });
  }, [files, handleFileUpload]);

  // Keyboard shortcut for send message
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        event.preventDefault();
        handleSend();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSend]);

  function getKeyboardShortcut() {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('win') || userAgent.includes('linux'))
      return 'Ctrl+Enter';
    if (userAgent.includes('mac')) return '⌘+Enter';
    return 'Ctrl+Enter';
  }

  function handleFilesUpload(uploadedFiles: FileList | File[]) {
    const newFiles = Array.from(uploadedFiles).map((file) => ({
      nativeFile: file,
      isUploading: false,
      fileUrl: null,
      localId: Math.random()?.toString(),
    }));

    setFiles((prev) => (prev ? [...prev, ...newFiles] : newFiles));
  }

  function handleComposerDrop(event: React.DragEvent) {
    event.preventDefault();
    setIsDragging(false);
    if (event.dataTransfer.files?.length) {
      handleFilesUpload(event.dataTransfer.files);
    }
  }

  function handleComposerDragOver(event: React.DragEvent) {
    // Only react to file drags — ignore text selections dragged within the editor.
    if (!event.dataTransfer.types?.includes('Files')) return;
    event.preventDefault();
    setIsDragging(true);
  }

  function handleComposerDragLeave(event: React.DragEvent) {
    // dragleave also fires when the cursor crosses onto a child node; ignore those.
    // relatedTarget is the element being entered — always a DOM Node here, so the cast is safe.
    const enteringNode = event.relatedTarget as Node | null;
    if (enteringNode && event.currentTarget.contains(enteringNode)) return;
    setIsDragging(false);
  }

  const handleAiRephrase = async () => {
    if (!content) return;

    setIsLoading(true);

    const res = await rephraseText(content);

    if (!res.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Failed to rephrase text',
        message: 'Please try again',
      });
      setIsLoading(false);
      return;
    }

    const rephrasedData = (await res.json()) as {
      originalText: string;
      rephrasedText: string;
    };

    setContent(rephrasedData.rephrasedText);

    mixpanel.track(MIXPANEL_EVENTS.AI_GENERATION_USED, {
      surface_area: MIXPANEL_SURFACE_AREA.THREADS,
    });

    setIsLoading(false);
  };

  const hasContent = !!content?.trim() || !!files?.length;

  if (isChatClosed) {
    return (
      <div className="py-lg px-xl flex flex-col gap-xl bg-bg-primary">
        <div className="flex items-center justify-between">
          <span className="txt-small-medium text-text-tertiary">
            This has now been closed. Reach out to Commenda support to resolve
            any queries.
          </span>
        </div>
      </div>
    );
  }

  // Renders the composer body for either the inline location or the modal.
  // Both stay mounted simultaneously when expanded so the inline view doesn't
  // collapse out of the thread, but they need separate refs to avoid React
  // clobbering them. The active editorRef (top of component) tracks which one
  // the handlers should drive.
  //
  // `surface` decides which height the editor gets — passing it instead of
  // reading `isExpanded` keeps the modal's editor at 60vh through the close
  // animation (otherwise it visibly shrinks before the modal fades out).
  const renderComposerBody = (
    editorRefForThis: React.Ref<RichTextEditorHandle>,
    buttonsRefForThis: React.Ref<HTMLDivElement> | null,
    surface: 'inline' | 'modal'
  ) => (
    <>
      {/* Attachment chips — spinner while the file uploads to S3, removable
          once its fileUrl resolves. */}
      {files?.length > 0 && (
        <div className="flex flex-wrap gap-md">
          {files.map((file) => (
            <AttachmentChip
              key={file.localId}
              fileName={file.nativeFile.name}
              status={getAttachmentStatus(file)}
              errorMessage={file.errorMessage}
              onRetry={
                file.errorMessage
                  ? () => handleFileRetry(file.localId)
                  : undefined
              }
              onRemove={() => handleFileRemove(file.localId)}
            />
          ))}
        </div>
      )}

      {/* Typing area */}
      <Suspense
        fallback={
          <div
            className="!p-0 !border-0 !shadow-none !rounded-none !min-h-0 w-full"
            style={{ minHeight: 20 }}
            aria-hidden="true"
          />
        }
      >
        <RichTextEditor
          ref={editorRefForThis}
          content={content}
          setContent={setContent}
          onAddFiles={handleFilesUpload}
          placeholder={
            canMentionAgent
              ? 'Type a message or @mention Commenda Agent'
              : 'Type your message...'
          }
          _className="!p-0 !border-0 !shadow-none !rounded-none !min-h-0"
          maxHeight={surface === 'modal' ? '60vh' : maxTextareaHeight}
          minHeight={surface === 'modal' ? '60vh' : undefined}
          showToolbar={showFormattingToolbar}
          mentionCandidates={mentionCandidates}
          canMentionAgent={canMentionAgent}
          onSelectionUpdate={(editor) => {
            setIsPlaceholderSelected(isSelectionAPlaceholder(editor));
          }}
        />
      </Suspense>

      {/* Actions bar */}
      <div
        ref={buttonsRefForThis ?? undefined}
        className="flex items-center justify-between"
      >
        {/* Left: action icons */}
        <div className="flex gap-lg items-center">
          {shouldHaveAdditionalControls ? (
            <>
              <Tooltip content="Attach files">
                <IconButton
                  icon={<Icons.Plus className="size-5" />}
                  onClick={() => fileInputRef.current?.click()}
                  aria-label="Attach files"
                  size="md"
                  disabled={isLoading}
                  className="!rounded-full bg-bg-secondary"
                />
              </Tooltip>
              <Tooltip
                content={
                  showFormattingToolbar ? 'Hide formatting' : 'Show formatting'
                }
              >
                <IconButton
                  icon={
                    <span
                      className="font-semibold text-sm leading-none tracking-tight"
                      aria-hidden="true"
                    >
                      Aa
                    </span>
                  }
                  onClick={() => setShowFormattingToolbar((prev) => !prev)}
                  aria-label={
                    showFormattingToolbar
                      ? 'Hide formatting toolbar'
                      : 'Show formatting toolbar'
                  }
                  aria-pressed={showFormattingToolbar}
                  size="md"
                  disabled={isLoading}
                  className={
                    showFormattingToolbar ? 'bg-bg-secondary-hover' : ''
                  }
                />
              </Tooltip>
              <Tooltip content="View response templates">
                <IconButton
                  icon={<Icons.Folder className="size-4" />}
                  onClick={templatesModalVisibility.show}
                  aria-label="View response templates"
                  size="md"
                  disabled={isLoading}
                />
              </Tooltip>
              <Tooltip content="Rephrase with AI">
                <IconButton
                  icon={<Icons.MagicWand01 className="size-4" />}
                  onClick={handleAiRephrase}
                  aria-label="Rephrase with AI"
                  size="md"
                  disabled={isLoading || !content?.trim()}
                />
              </Tooltip>
            </>
          ) : (
            <Tooltip content="Attach files">
              <IconButton
                icon={<Icons.Attachment01 className="size-4" />}
                onClick={() => fileInputRef.current?.click()}
                aria-label="Attach files"
                size="md"
                disabled={isLoading}
              />
            </Tooltip>
          )}
        </div>

        {/* Right: expand toggle + send button */}
        <div className="flex items-center gap-md">
          <Tooltip content={isExpanded ? 'Minimize' : 'Expand'}>
            <IconButton
              icon={isExpanded ? <MinimizeIcon /> : <ExpandIcon />}
              onClick={() => setIsExpanded((prev) => !prev)}
              aria-label={isExpanded ? 'Minimize composer' : 'Expand composer'}
              aria-pressed={isExpanded}
              size="md"
              disabled={isLoading}
            />
          </Tooltip>
          <ChatSendButton
            onClick={handleSend}
            disabled={!hasContent || isLoading || hasPendingFileUploads()}
            aria-label={`Send message (${getKeyboardShortcut()})`}
          />
        </div>
      </div>
    </>
  );

  return (
    <>
      <TemplatesModal
        isOpen={templatesModalVisibility.visible}
        onClose={templatesModalVisibility.hide}
        templates={THREADS_CHAT_TEMPLATES}
        onSelectTemplate={onSelectTemplate}
      />

      <input
        type="file"
        multiple
        ref={fileInputRef}
        className="hidden"
        onChange={(e) => {
          if (e.target.files) {
            handleFilesUpload(e.target.files);
          }
          e.currentTarget.value = '';
        }}
      />

      {/* Inline composer — always rendered so expanding doesn't remove the
          composer from the thread. The modal below renders the active editor
          on top of it when expanded. */}
      <div className="flex flex-col pb-lg px-lg">
        {selectedReplyMessage && (
          <ReplyMessagePreview
            replyMessage={selectedReplyMessage}
            removeReplyMessage={removeSelectedReplyMessage}
          />
        )}

        <div
          ref={containerRef}
          onDrop={handleComposerDrop}
          onDragOver={handleComposerDragOver}
          onDragLeave={handleComposerDragLeave}
          className={cn(
            'bg-bg-primary border rounded-2xl shadow-[0px_2px_24px_0px_rgba(0,0,0,0.04)] p-xl flex flex-col gap-2xl',
            isDragging
              ? 'border-2 border-dashed border-border-primary'
              : 'border-border-secondary'
          )}
        >
          {renderComposerBody(inlineEditorRef, buttonsRef, 'inline')}
        </div>
      </div>

      <Dialog open={isExpanded} onOpenChange={setIsExpanded}>
        <DialogContent
          hideCloseIcon
          className="max-w-3xl w-[90vw] p-xl flex flex-col gap-2xl"
        >
          {selectedReplyMessage && (
            <ReplyMessagePreview
              replyMessage={selectedReplyMessage}
              removeReplyMessage={removeSelectedReplyMessage}
            />
          )}
          {renderComposerBody(modalEditorRef, null, 'modal')}
        </DialogContent>
      </Dialog>
    </>
  );
};

// do not exist on the Kakashi's Icon component
function ExpandIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="15 3 21 3 21 9" />
      <polyline points="9 21 3 21 3 15" />
      <line x1="21" y1="3" x2="14" y2="10" />
      <line x1="3" y1="21" x2="10" y2="14" />
    </svg>
  );
}

// do not exist on the Kakashi's Icon component
function MinimizeIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="4 14 10 14 10 20" />
      <polyline points="20 10 14 10 14 4" />
      <line x1="14" y1="10" x2="21" y2="3" />
      <line x1="3" y1="21" x2="10" y2="14" />
    </svg>
  );
}

export default ChatInput;
